/**
 * Pure column-header layout helpers for {@link ./TableHeaderColumns}.
 *
 * Extracted verbatim from the component so header label wrapping, multi-column
 * sort ordering, and the select-all state are testable without a DOM or the
 * SimpleTable context. Behavior must stay identical.
 */

export type HeaderLabelLayout =
  | {
      type: "break";
      /** The first two words, rendered on their own line. */
      firstLine: string;
      /** Everything after the forced break (may be empty). */
      rest: string;
      enableWrap: boolean;
    }
  | {
      type: "normal";
      label: string;
      enableWrap: boolean;
    };

/**
 * Decide how a column header label wraps.
 *
 * Rules (unchanged from the original inline `useMemo`):
 * - >2 words → allow wrapping.
 * - ≥2 words whose first two are longer than 9 characters combined → force a
 *   break after the second word, so a long two-word head doesn't squeeze the
 *   rest of the header out of the cell.
 * - Otherwise render on one line with `whitespace-nowrap`.
 *
 * A non-string label is coerced with `String()` (and NOT trimmed) exactly as
 * the component did.
 */
export function computeHeaderLabelLayout(rawLabel: unknown): HeaderLabelLayout {
  const label = typeof rawLabel === "string" ? rawLabel.trim() : String(rawLabel);
  const words = label.split(/\s+/);
  const hasMoreThanTwoWords = words.length > 2;
  const hasAtLeastTwoWords = words.length >= 2;
  const firstTwoLength = hasAtLeastTwoWords
    ? (words[0] + words[1]).length
    : words[0]?.length || 0;
  const shouldForceBreakAfterSecond = hasAtLeastTwoWords && firstTwoLength > 9;
  const enableWrap = hasMoreThanTwoWords || shouldForceBreakAfterSecond;

  if (shouldForceBreakAfterSecond) {
    return {
      type: "break",
      firstLine: words.slice(0, 2).join(" "),
      rest: words.slice(2).join(" "),
      enableWrap,
    };
  }

  return { type: "normal", label, enableWrap };
}

export interface SortColumnDescriptor {
  field: string;
  direction: "asc" | "desc";
}

export interface SortColumnBadge {
  direction: "asc" | "desc";
  /** 1-based position in the multi-column sort, shown in the header badge. */
  priority: number;
}

/**
 * Index the active multi-column sort by field so a header can look up its own
 * arrow + priority badge in O(1). Later duplicates of a field win, matching the
 * original `Map.set` loop.
 */
export function buildSortColumnMap(
  sortColumns: ReadonlyArray<SortColumnDescriptor>
): Map<string, SortColumnBadge> {
  const map = new Map<string, SortColumnBadge>();
  sortColumns.forEach((col, index) => {
    map.set(col.field, { direction: col.direction, priority: index + 1 });
  });
  return map;
}

/**
 * Select-all checkbox state. An empty page is never "all selected" — otherwise
 * the header checkbox reads as checked on a table with nothing in it.
 */
export function isAllRowsSelected(selectedCount: number, rowCount: number): boolean {
  return selectedCount === rowCount && rowCount > 0;
}
